import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Title } from '@angular/platform-browser';

import { BranchService } from 'src/app/services/branch/branch.service';
import { DropdownMenus } from 'src/app/services/dropdowns/dropdowns.model';
import {
  ApplicantOwnAnyVehicles,
  FinalLoanApplication,
} from 'src/app/services/loan/loan.model';
import { LoanService } from 'src/app/services/loan/loan.service';
import { ModalFinishByPhoneComponent } from 'src/app/components/modals/modal-finish-by-phone/modal-finish-by-phone.component';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { handleErrorMessage } from 'src/app/helpers/error-handler.helper';
import { ErrorMessageService } from 'src/app/services/error-message/error-message.service';
import { phoneNumberValidator } from 'src/app/directives/phone-number/phone-number.directive';
import { zipCodeValidator } from 'src/app/directives/zip-code/zip-code.directive';
import { DataLayerService } from 'src/app/services/analytics/data-layer.service';
import { emailValidator } from 'src/app/directives/email/email.directive';
import { RouteName } from '../loan-routing.module';

@Component({
  selector: 'app-applicant',
  templateUrl: './applicant.component.html',
  styleUrls: ['./applicant.component.scss'],
})
export class ApplicantComponent implements OnInit, OnDestroy {
  applicantFormGroup: FormGroup;
  loanApplication: FinalLoanApplication | null = null;
  vehiclesOwned: ApplicantOwnAnyVehicles | null = null;
  dropdownMenus: DropdownMenus | null = null;
  isSaving = false;
  isDirtyAndMissingRequiredFields = false;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private loanService: LoanService,
    private navService: NavigationService,
    private branchService: BranchService,
    private router: Router,
    private dropdownsService: DropdownsService,
    private modalService: NgbModal,
    private errorMessageService: ErrorMessageService,
    private titleService: Title,
    private dataLayerService: DataLayerService
  ) {
    this.titleService.setTitle('Eagle Loan | Applicant Information');
    this.navService.setBackNav('Choose Branch', RouteName.branch);
    this.branchService.setShowInfoButton(true);

    this.applicantFormGroup = this.formBuilder.group({
      ContactFirst: ['', Validators.required],
      ContactLast: ['', Validators.required],
      ContactMiddle: [''],
      ContactSuffix: [''],
      MaritalStatusCD: ['', Validators.required],
      Dependents: ['', [Validators.required, Validators.min(0)]],
      Address: ['', Validators.required],
      AptNumber: [''],
      POBox: [''],
      City: ['', Validators.required],
      State: ['', Validators.required],
      ZipCode: ['', [Validators.required, zipCodeValidator]],
      EmploymentStatus: ['', Validators.required],
      LivingSituation: ['', Validators.required],
      Years: ['', [Validators.required, Validators.min(0)]],
      Months: [
        '',
        [Validators.required, Validators.max(12), Validators.min(0)],
      ],
      OwnAnyVehicles: ['', Validators.required],
      NumberOfVehicles: ['', Validators.required],
      PhoneNumber: ['', [Validators.required, phoneNumberValidator]],
      PhoneNumberType: ['', Validators.required],
      EmailAddress: ['', [Validators.required, emailValidator]],
      Over18: [false, Validators.requiredTrue],
      PrivacyPolicy: [false, Validators.requiredTrue],
      CommunicationConsent: [false],
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.loanService.finalLoanApplication.subscribe(
        (data: FinalLoanApplication | null) => {
          if (data) {
            this.loanApplication = { ...data };
          }
        }
      )
    );

    this.subscriptions.push(
      this.dropdownsService.dropdownMenus.subscribe(
        (data: DropdownMenus | null) => {
          this.dropdownMenus = null;

          if (data) {
            this.dropdownMenus = { ...data };
          }
        }
      )
    );

    this.subscriptions.push(
      this.applicantFormGroup.statusChanges.subscribe((status: string) => {
        if (status && status === 'VALID') {
          this.isDirtyAndMissingRequiredFields = false;
        }
      })
    );

    // On refresh of the page it will redirect to start of application.
    if (!this.loanApplication) {
      this.router.navigateByUrl(RouteName.default, { replaceUrl: true });
    }

    if (!this.dropdownMenus) {
      this.dropdownsService.getDropdowns();
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  async onNext(): Promise<void> {
    this.isDirtyAndMissingRequiredFields = false;

    if (this.applicantFormGroup.invalid) {
      this.applicantFormGroup.markAllAsTouched();
      this.isDirtyAndMissingRequiredFields = true;
      return;
    }

    this.dataLayerService.logEvent(
      'button_clicked',
      'loan_application',
      'click',
      'Save Consent of Applicant Information'
    );

    const loan = this.loanService.setApplicantInformation(
      this.applicantFormGroup.value
    );

    await this.saveLoanApplication(loan);
  }

  async onFinishByPhone(): Promise<void> {
    this.isDirtyAndMissingRequiredFields = false;

    if (this.applicantFormGroup.invalid) {
      this.applicantFormGroup.markAllAsTouched();
      this.isDirtyAndMissingRequiredFields = true;
      return;
    }

    this.dataLayerService.logEvent(
      'button_clicked',
      'loan_application',
      'click',
      'Finish By Phone | Applicant Information'
    );

    const modalRef = this.modalService.open(ModalFinishByPhoneComponent);

    modalRef.componentInstance.loan = this.loanService.setApplicantInformation(
      this.applicantFormGroup.value
    );

    const result = await modalRef.result;

    if (result && result.status === 'confirmed') {
      this.onConfirmed();
    }
  }

  onConfirmed() {
    this.loanService.clearApplication();
    this.router.navigateByUrl(RouteName.skippedConfirmation);
  }

  private async saveLoanApplication(loan: FinalLoanApplication): Promise<void> {
    try {
      this.isSaving = true;

      const response = await this.loanService.postLoanApplication(loan);

      if (response) {
        this.loanService.setLoanAfterPost(response);
        this.router.navigateByUrl(RouteName.applicantHomeVehicles);
      }

      return;
    } catch (error) {
      this.errorMessageService.addToErrorMessages(handleErrorMessage(error));
    } finally {
      this.isSaving = false;
    }
  }
}
